<template>
  <div test-id="main">
    <div v-if="isLoading">
      <swap-form-loader />
    </div>
    <div class="border border-gray-70 rounded-2xl drop-shadow" v-else>
      <div class="md:text-base text-sm border-b border-gray-70">
        <p class="md:px-10 md:py-4 p-4 font-medium">Add Swap Details</p>
      </div>
      <div class="md:px-10 md:py-8 p-4">
        <!-- send -->
        <div>
          <el-form
            :model="swapForm"
            :rules="rules"
            ref="swapForm"
            size="normal"
          >
            <div class="mb-6">
              <p class="text-root-2 md:text-sm text-xs mb-2">Send</p>
              <div
                class="form-content border border-gray-70 rounded-lg py-1 bg-root"
              >
                <div
                  class="form-1-element text-xs font-normal text-gray-40 pl-5 flex items-center"
                >
                  You Send
                </div>
                <div class="form-2-element" id="swapInput">
                  <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
                  <el-form-item
                    prop="enteredAmount"
                    class="flex justify-end items-center"
                    id="bridgeTourItem1"
                  >
                    <el-input
                      class="m-0 placeholder-end"
                      @input="enteredAmountInput"
                      v-model.trim="swapForm.enteredAmount"
                      placeholder="0.00"
                    ></el-input>
                  </el-form-item>
                </div>
                <div
                  class="form-3-element border-l-2 border-gray-70 flex -my-1 text-xs md:text-sm font-normal"
                >
                  <select-swap-asset swap="from" />
                </div>
              </div>
            </div>
            <div class="flex justify-between my-3">
              <div class="flex items-center">
                <fee-icon />
                <div
                  v-if="fromSwapAssetDetails"
                  class="text-primary -ml-3 bg-orange-3 rounded-full px-3 py-2 text-xs md:text-sm font-normal flex items-center gap-x-2"
                >
                  Fee: {{ displayFee }}
                </div>
              </div>
              <el-tooltip
                class="item"
                effect="dark"
                content="Reset asset selections"
                placement="top-start"
              >
                <div @click="resetOptions" class="cursor-pointer">
                  <reset-icon />
                </div>
              </el-tooltip>
            </div>

            <!-- Receive -->
            <div class="mt-4 mb-10">
              <p class="md:text-sm text-xs mb-2">Receive Minimum</p>
              <div
                class="form-content border border-gray-70 rounded-lg py-1 bg-root"
              >
                <div
                  class="text-xs font-normal text-gray-40 pl-5 flex items-center"
                >
                  You get
                </div>
                <div class="" id="swapInput">
                  <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
                  <el-form-item
                    class="flex justify-end items-center"
                    prop="recipientAmount"
                    id="bridgeTourItem2"
                  >
                    <el-input
                      @input="recipientAmountInput"
                      class="m-0 placeholder-end .el-input__inner"
                      v-model.trim="swapForm.recipientAmount"
                      placeholder="0.00"
                    ></el-input>
                  </el-form-item>
                </div>
                <div
                  class="form-3-element border-l-2 border-gray-70 flex -my-1 text-xs md:text-sm font-normal"
                >
                  <select-swap-asset swap="to" />
                </div>
              </div>
            </div>
            <div class="w-full" v-if="!currentSwapBeneficiaryDetails">
              <!-- The id in the next line maps the walkthrough to that el-form-item element. Please do not remove this id -->
              <el-tooltip
                content="You do not have the access to initiate a swap"
                placement="top"
                :disabled="hasInitiatorAccess"
              >
                <el-form-item class="w-full" id="bridgeTourItem3">
                  <el-button
                    @click="validateForm('swapForm')"
                    :disabled="!disableBeneficiaryButton || !hasInitiatorAccess"
                    class="bg-primary text-white w-full rounded-xl md:py-4 py-3 md:text-base text-xs font-light"
                    >Select Beneficiary Address</el-button
                  >
                </el-form-item>
              </el-tooltip>
            </div>

            <!-- beneficiary form -->
            <div
              v-if="currentSwapBeneficiaryDetails"
              class="border-t border-gray-70 md:-mx-10 rounded-b"
            >
              <div class="shadow-inner mt-5 md:px-10 px-4 py-6">
                <div class="flex justify-between items-center mb-4">
                  <p class="font-medium">Wallet Details</p>
                  <p
                    @click="openBeneficiaryDrawer"
                    class="w-44 border rounded-lg sm font-light cursor-pointer border-gray-70 p-3 flex items-center justify-center"
                  >
                    Use Another Beneficiary
                  </p>
                </div>
                <div id="beneficaryInput">
                  <el-form-item label="Wallet Tag">
                    <el-input
                      class="w-full capitalize"
                      v-model="swapForm.name"
                      disabled
                    >
                    </el-input
                  ></el-form-item>
                  <el-form-item label="Wallet Address">
                    <el-input
                      v-model.trim="swapForm.walletAddress"
                      disabled
                      class="input-with-prefix"
                    >
                      <template slot="prefix">
                        <div class="mt-4">
                          <network-icon
                            :currency="
                              getNetworkFullName(
                                currentSwapBeneficiaryDetails.network
                              )
                            "
                            size="lg:h-6 lg:w-6 h-4 w-4"
                          />
                        </div>
                      </template>
                    </el-input>
                  </el-form-item>
                </div>
                <div class="w-full my-4">
                  <el-form-item class="w-full">
                    <el-button
                      :loading="isLoading"
                      @click="initiateSwapHandler"
                      class="bg-primary text-white w-full rounded-xl md:py-4 py-2 text-base font-light"
                      >Swap</el-button
                    >
                  </el-form-item>
                </div>
              </div>
            </div>
          </el-form>
        </div>

        <beneficiary-drawer
          v-if="showBeneficiaryDrawer"
          :showDrawer="showBeneficiaryDrawer"
          :showBeneficiaryForm="showBeneficiaryForm"
          :loading="isLoadingDialog"
          @showBeneficiaryList="showBeneficiaryList"
          @addNewBeneficiary="addNewBeneficiary"
          @walletDetailsHandler="addWalletDetails"
          @openVerifyAuthDialog="showDialog('showVerifyAuthDialog')"
          @closeDrawer="closeDrawer('showBeneficiaryDrawer')"
        />

        <verify-auth-dialog
          v-if="showVerifyAuthDialog"
          :authState="authState"
          :showDialog="showVerifyAuthDialog"
          @closeDialog="closeDialog('showVerifyAuthDialog')"
          @createSwapBeneficiary="createSwapBeneficiary"
        />

        <pending-beneficiary-dialog
          v-if="showPendingBeneficiaryDialog"
          :showDialog="showPendingBeneficiaryDialog"
          @closeDialog="closeDialog('showPendingBeneficiaryDialog')"
          @processingBeneficiaryHandler="processingBeneficiaryHandler"
          @closeProcessingBeneficiaryHandler="processingBeneficiaryHandler"
        />
        <beneficiary-added-dialog
          v-if="showBeneficiaryAddedDialog"
          :showDialog="showBeneficiaryAddedDialog"
          @closeDialog="closeDialog('showBeneficiaryAddedDialog')"
          @processingBeneficiaryHandler="processingBeneficiaryHandler"
        />
      </div>
    </div>
    <app-tour
      :tourDetails="bridgeTour"
      tourComponent="bridge"
      :displayTour="displayTour"
      @stopTour="stopTour"
    />
    <initiate-tour-button
      @startTour="startBridgeTour"
      v-if="tenantPermission('bridge-access')"
    />
  </div>
</template>
<script>
import tenant from '@/mixins/tenant.js'
import { BRIDGE_TOUR } from '@/utils/constants'
import {
  GetNetworkFullName,
  AddComma,
  FormatAmount,
  ValidateInput,
  ValidateSwiftCode
} from '@/utils/helpers'
import fetchAllTours from '@/mixins/appTourApi.js'
import swapPairAssetsDetails from '@/mixins/bridge.js'
import httpErrorMessage from '@/mixins/global.js'
import userKinds from '@/mixins/userKinds.js'

export default {
  mixins: [
    swapPairAssetsDetails,
    fetchAllTours,
    tenant,
    httpErrorMessage,
    userKinds
  ],
  data () {
    return {
      displayTour: false,
      bridgeTour: BRIDGE_TOUR,
      firstInputSelected: null,
      isLoadingDialog: false,
      authState: 'swapBeneficiaryFormState',
      showBeneficiaryDrawer: false,
      showBeneficiaryForm: false,
      showVerifyAuthDialog: false,
      showPendingBeneficiaryDialog: false,
      showBeneficiaryAddedDialog: false,
      sendAsset: null,
      sendNetwork: null,
      receiveAsset: null,
      receiveNetwork: null,
      isAssetDropdown: true,
      feeValue: null,
      payload: {
        page: 1,
        size: 5
      },
      swapForm: {
        enteredAmount: '',
        recipientAmount: '',
        name: '',
        walletAddress: '',
        fromAsset: '',
        toAsset: '',
        fromNetwork: '',
        toNetwork: '',
        isValidEnteredAmount: false,
        isValidRecipientAmount: false
      },
      rules: {
        enteredAmount: {
          validator: this.validateEnteredAmount,
          trigger: ['blur', 'change']
        },
        recipientAmount: {
          validator: this.validateRecipientAmount,
          trigger: ['blur', 'change']
        },
        name: {
          validator: ValidateInput,
          trigger: ['blur', 'change']
        },
        walletAddress: {
          validator: ValidateSwiftCode,
          trigger: ['blur', 'change']
        }
      },
      isLoading: false
    }
  },
  components: {
    InitiateTourButton: () =>
      import('@/components/v2/app-tour/InitiateTourButton.vue'),
    AppTour: () => import('@/components/v2/app-tour/AppTour.vue'),
    ResetIcon: () => import('@/assets/v2/icons/ResetIcon'),
    FeeIcon: () => import('@/assets/v2/icons/FeeIcon'),
    NetworkIcon: () => import('@/assets/v2/icons/NetworkIcons.vue'),
    BeneficiaryDrawer: () =>
      import('@/components/v2/bridge/drawers/BeneficiaryDrawer.vue'),
    VerifyAuthDialog: () => import('@/components/v2/auth/VerifyAuthDialog.vue'),
    PendingBeneficiaryDialog: () =>
      import('@/components/v2/bridge/dialogs/PendingBeneficiaryDialog.vue'),
    BeneficiaryAddedDialog: () =>
      import('@/components/v2/bridge/dialogs/BeneficiaryAddedDialog.vue'),
    SelectSwapAsset: () => import('@/components/v2/bridge/SelectSwapAsset.vue'),
    SwapFormLoader: () =>
      import('@/components/v2/skeletalLoaders/SwapFormLoader.vue')
  },
  computed: {
    currentSwapBeneficiaryDetails () {
      return this.$store.state.bridge.currentSwapBeneficiaryDetails
    },
    beneficiaryDetailsForm () {
      return this.$store.state.bridge.beneficiaryDetailsForm
    },
    swapFormDetails () {
      return this.$store.state.bridge.swapFormDetails
    },
    fee: function () {
      if (this.swapPairAssetsDetails && this.swapForm.enteredAmount) {
        return this.swapPairAssetsDetails?.feeFlat
          ? { value: this.swapPairAssetsDetails.feeFlat, type: 'feeFlat' }
          : {
              value: this.swapPairAssetsDetails.feePercentage,
              type: 'feePercentage'
            }
      } else {
        return this.swapPairAssetsDetails?.feeFlat
          ? { value: this.swapPairAssetsDetails?.feeFlat, type: 'feeFlat' }
          : this.swapPairAssetsDetails?.feePercentage
          ? {
              value: this.swapPairAssetsDetails?.feePercentage,
              type: 'feePercentage'
            }
          : { value: 0, type: '' }
      }
    },
    feeCap () {
      return this.swapPairAssetsDetails?.feeCap || 0
    },
    displayFee () {
      // if feePercentage
      if (this.fee.type === 'feePercentage') {
        // consider computation for feeCap first
        return this.feeCap === this.fees
          ? `${this.fee.value}%(capped at ${
              (this.fees || 0) + ' ' + this.fromSwapAssetDetails.currency
            }) ~ ${(this.fees || 0) + ' ' + this.fromSwapAssetDetails.currency}`
          : `${this.fee.value}% ~ ${
              (this.fees || 0) + ' ' + this.fromSwapAssetDetails.currency
            }`
      } else {
        return (
          this.fee.value +
          (this.fee.type === 'feePercentage'
            ? '%'
            : this.fromSwapAssetDetails.currency)
        )
      }
    },
    fees () {
      let fee
      if (this.firstInputSelected) {
        fee =
          this.fee.type === 'feeFlat'
            ? this.fee.value
            : this.feeValueToParseFloatPercentage(
                parseFloat(this.swapForm.enteredAmount)
              )
      } else {
        fee =
          this.fee.type === 'feeFlat'
            ? this.fee.value
            : this.feeValueToParseFloatPercentage(
                parseFloat(this.swapForm.recipientAmount)
              )
      }
      const realFee = this.feeCap && fee > this.feeCap ? this.feeCap : fee
      return realFee
    },
    recipientAmount: function () {
      const realFee =
        this.feeCap && this.fees > this.feeCap ? this.feeCap : this.fees
      const recipientAmount =
        FormatAmount(this.swapForm.enteredAmount) - realFee
      return AddComma(parseFloat(recipientAmount).toFixed(2).toString())
    },
    enteredAmount: function () {
      const realFee =
        this.feeCap && this.fees > this.feeCap ? this.feeCap : this.fees
      const enteredAmount =
        FormatAmount(this.swapForm.recipientAmount) + realFee
      return AddComma(parseFloat(enteredAmount).toFixed(2).toString())
    },
    disableBeneficiaryButton () {
      return (
        this.isFromAssetSelected &&
        this.isToAssetSelected &&
        this.swapForm.enteredAmount &&
        this.swapForm.recipientAmount
      )
    },
    isANewUser () {
      return !this.$store.state.appTour.allTours.bridge
    }
  },
  methods: {
    startBridgeTour () {
      this.$tours.bridgeTour.start()
      this.displayTour = true
    },
    stopTour () {
      this.displayTour = false
    },
    // calculates fee.value percentage with respect to the argument passed
    feeValueToParseFloatPercentage (arg) {
      return parseFloat(((this.fee.value / 100) * arg).toFixed(2))
    },
    closeDialog (arg) {
      this[arg] = false
    },
    showDialog (arg) {
      this[arg] = true
    },
    processingBeneficiaryHandler () {
      this.closeDialog('showPendingBeneficiaryDialog')
      if (this.routeName === 'settings') {
        this.$router.push({ name: 'bridge' })
      } else {
        this.showBeneficiaryList()
      }
    },
    async createSwapBeneficiary (twoFaCode) {
      this.isLoadingDialog = true
      this.showVerifyAuthDialog = false
      this.beneficiaryDetailsForm.twoFaCode = twoFaCode.code
      const payload = { ...this.beneficiaryDetailsForm }
      payload.assetType = payload.assetType.toUpperCase()
      await this.$store
        .dispatch('bridge/createBeneficiary', payload)
        .then(() => {
          this.isLoadingDialog = false
          this.showBeneficiaryAddedDialog = true
          this.showBeneficiaryList()
          this.getSavedBeneficiaries()
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoadingDialog = false
        })
    },
    getNetworkFullName (val) {
      return GetNetworkFullName(val)
    },
    addNewBeneficiary () {
      this.showBeneficiaryForm = true
    },
    showBeneficiaryList () {
      this.showBeneficiaryForm = false
    },
    openBeneficiaryDrawer () {
      this.showBeneficiaryDrawer = true
    },
    selectSendAsset (asset) {
      this.sendAsset = asset
      this.isAssetDropdown = false
    },
    selectReceiveAsset (asset) {
      this.receiveAsset = asset
      this.isAssetDropdown = false
    },
    selectSendNetwork (network) {
      this.sendNetwork = network
      this.isAssetDropdown = true
    },
    selectReceiveNetwork (network) {
      this.receiveNetwork = network
      this.isAssetDropdown = true
    },
    resetOptions () {
      this.$store.dispatch('bridge/clearMainSwapState')
    },
    addWalletDetails () {
      this.swapForm.name = this.currentSwapBeneficiaryDetails.tag
      this.swapForm.walletAddress =
        this.currentSwapBeneficiaryDetails.walletAddress
      this.$store.dispatch('bridge/setSwapFormDetails', this.swapForm)
    },
    closeDrawer (arg) {
      this[arg] = false
    },
    goToConfirmationScreens () {
      this.$store.dispatch('bridge/setSwapFormDetails', this.swapForm)
      this.$emit('next')
    },
    async initiateSwapHandler () {
      if (
        !this.swapForm.isValidEnteredAmount &&
        !this.swapForm.isValidRecipientAmount
      ) {
        this.$notify({
          showClose: true,
          message: 'Please fill all inputs correctly',
          type: 'error'
        })
        return
      }
      const swapDetails = {
        walletAddressID: this.currentSwapBeneficiaryDetails.id,
        amount: parseFloat(this.swapForm.enteredAmount),
        pairID: this.swapPairAssetsDetails.id
      }
      await this.initiateSwap(swapDetails)
    },
    validateEnteredAmount (rule, value, callback) {
      const enteredAmount = FormatAmount(value)
      if (enteredAmount < 10) {
        this.swapForm.isValidEnteredAmount = false
        callback(new Error('Minimum amount is 10'))
      } else if (enteredAmount > 9999999999) {
        this.swapForm.isValidEnteredAmount = false
        callback(new Error(`Maximum amount is  ${AddComma('9999999999')}`))
      } else {
        this.swapForm.isValidEnteredAmount = true
        callback()
      }
    },
    validateRecipientAmount (rule, value, callback) {
      const recipientAmount = FormatAmount(value)
      if (recipientAmount > 9999999999) {
        this.swapForm.isValidRecipientAmount = false
        callback(new Error(`Maximum amount is  ${AddComma('9999999999')}`))
      } else {
        this.swapForm.isValidRecipientAmount = true
        callback()
      }
    },
    enteredAmountInput () {
      this.firstInputSelected = true
      if (this.swapForm.enteredAmount) {
        this.swapForm.recipientAmount = this.recipientAmount
      } else {
        this.swapForm.recipientAmount = ''
      }
    },
    recipientAmountInput () {
      this.firstInputSelected = false
      if (this.swapForm.recipientAmount) {
        this.swapForm.enteredAmount = this.enteredAmount
      } else {
        this.swapForm.enteredAmount = ''
      }
    },
    validateForm (formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          this.$store.dispatch('bridge/setSwapFormDetails', this.swapForm)
          this.openBeneficiaryDrawer()
        } else {
          this.$notify({
            showClose: true,
            message: 'Please fill all inputs correctly',
            type: 'error'
          })
        }
      })
    }
  },
  watch: {
    'swapForm.enteredAmount': function (newValue) {
      if (!this.firstInputSelected) {
        this.swapForm.enteredAmount = newValue
      } else {
        const result = newValue.toString()
        const valid = /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/
        if (valid.test(result)) {
          this.swapForm.enteredAmount = result
        } else {
          this.swapForm.enteredAmount =
            FormatAmount(this.swapForm.enteredAmount) || null
        }
      }
    },
    'swapForm.recipientAmount': function (newValue) {
      if (this.firstInputSelected) {
        this.swapForm.recipientAmount = newValue
      } else {
        const result = newValue.toString()
        const valid = /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/
        if (valid.test(result)) {
          this.swapForm.recipientAmount = result
        } else {
          this.swapForm.recipientAmount =
            FormatAmount(this.swapForm.recipientAmount) || null
        }
      }
    },
    'fee.value': function (newValue) {
      this.enteredAmountInput()
    },
    deep: true
  },
  async mounted () {
    if (this.currentSwapBeneficiaryDetails) {
      this.swapForm.name = this.currentSwapBeneficiaryDetails.tag
      this.swapForm.walletAddress =
        this.currentSwapBeneficiaryDetails.walletAddress
    }
    if (this.swapFormDetails) {
      this.swapForm = { ...this.swapFormDetails }
    }
    await this.fetchAllTours()
    if (this.isANewUser) {
      this.startBridgeTour()
    }
  }
}
</script>

<style scoped>
#swapInput >>> .el-input__inner {
  border: none;
  outline: none;
  padding-right: 0rem;
  background: transparent;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-2);
  margin: 0;
}

::-webkit-outer-spin-button,
>>> .el-input__inner::-webkit-inner-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}

>>> .el-input__inner {
  -moz-appearance: textfield !important;
}

#beneficaryInput >>> .el-input.is-disabled .el-input__inner {
  background: #f6f6f6;
  border-color: #008a37;
  color: #424242;
  font-weight: 200;
}

#beneficaryInput >>> .el-input__inner {
  border: 1px solid #008a37;
  border-radius: 5px;
  margin-bottom: 10px;
  background: #f6f6f6;
  font-size: 0.8rem;
  height: 55px;
  text-transform: capitalize;
}

#beneficaryInput >>> .el-form-item__label {
  font-weight: 300;
  line-height: 30px;
  font-size: 0.8rem;
}

.input-with-prefix >>> .el-input__inner {
  padding-left: 40px;
}

>>> .el-form-item__error {
  padding-top: 12px;
}

>>> .el-input__prefix {
  left: 10px;
}

>>> .el-form-item {
  margin: 0;
}

.sm {
  font-size: 0.7rem;
}

>>> .placeholder-end .el-input__inner::-webkit-input-placeholder {
  text-align: right;
  position: absolute;
  font-size: 1.2rem;
  padding-top: 11px;
  margin-right: 8px;
  right: 0;
  color: var(--text-2);
}

.form-content {
  display: grid;
  grid-template-columns: 14% 51% 35%;
}
</style>
